import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SortingState } from '@tanstack/react-table';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { Pagination } from '@/components/data-display/Pagination';
import { useHistory } from '@/features/history/useHistory';
import { HistoryFiltersPanel } from '@/features/history/HistoryFilters';
import { HistoryTable } from '@/features/history/HistoryTable';
import { NewOperationButton } from '@/features/operations';
import { HistoryDetailModal } from '@/features/history/HistoryDetailModal';
import type { TransactionDto } from '@/features/history/types';
import { EventTimeline, type EventTimelineKind } from '@/features/audit-trail';

interface HistoryTabProps {
  clientId: string;
}

interface EventsTarget {
  kind: EventTimelineKind;
  parentId: number;
  transactionId: string;
}

function eventsTargetFromTransaction(tx: TransactionDto): EventsTarget | null {
  if (typeof tx.id !== 'number') return null;
  if (tx.transaction_type === 'payment') {
    return { kind: 'payment', parentId: tx.id, transactionId: tx.transaction_id };
  }
  if (tx.transaction_type === 'fx_trade') {
    return { kind: 'fx-trade', parentId: tx.id, transactionId: tx.transaction_id };
  }
  if (tx.transaction_type === 'incoming') {
    return { kind: 'incoming-payment', parentId: tx.id, transactionId: tx.transaction_id };
  }
  return null;
}

export function HistoryTab({ clientId }: HistoryTabProps) {
  const transactions = useHistory((s) => s.transactions);
  const isLoading = useHistory((s) => s.isLoading);
  const error = useHistory((s) => s.error);
  const total = useHistory((s) => s.total);
  const filters = useHistory((s) => s.filters);
  const page = useHistory((s) => s.page);
  const limit = useHistory((s) => s.limit);
  const sortBy = useHistory((s) => s.sortBy);
  const sortDirection = useHistory((s) => s.sortDirection);
  const fetchTransactions = useHistory((s) => s.fetchTransactions);
  const setFilters = useHistory((s) => s.setFilters);
  const clearFilters = useHistory((s) => s.clearFilters);
  const setPage = useHistory((s) => s.setPage);
  const setLimit = useHistory((s) => s.setLimit);
  const setSortBy = useHistory((s) => s.setSortBy);
  const clearError = useHistory((s) => s.clearError);

  const [selectedTransaction, setSelectedTransaction] = useState<TransactionDto | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [eventsTarget, setEventsTarget] = useState<EventsTarget | null>(null);

  useEffect(() => {
    void fetchTransactions(clientId);
  }, [clientId, fetchTransactions]);

  const handlePageChange = useCallback(
    (next: number) => {
      setPage(next);
      void fetchTransactions(clientId);
    },
    [clientId, setPage, fetchTransactions],
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      setLimit(size);
      void fetchTransactions(clientId);
    },
    [clientId, setLimit, fetchTransactions],
  );

  const handleSearch = useCallback(() => {
    void fetchTransactions(clientId);
  }, [clientId, fetchTransactions]);

  const handleClearFilters = useCallback(() => {
    clearFilters();
    void fetchTransactions(clientId);
  }, [clientId, clearFilters, fetchTransactions]);

  const handleClearSearch = useCallback(() => {
    setFilters({ searchQuery: '' });
    void fetchTransactions(clientId);
  }, [clientId, setFilters, fetchTransactions]);

  const dateError = useMemo(() => {
    const { dateFrom, dateTo } = filters;
    if (dateFrom && dateTo && dateFrom > dateTo) {
      return 'End date must be after start date.';
    }
    return null;
  }, [filters]);

  const sorting: SortingState = useMemo(
    () => (sortBy ? [{ id: sortBy, desc: sortDirection === 'desc' }] : []),
    [sortBy, sortDirection],
  );

  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      if (newSorting.length > 0) {
        const col = newSorting[0];
        setSortBy(col.id, col.desc ? 'desc' : 'asc');
      } else {
        setSortBy('', 'desc');
      }
      void fetchTransactions(clientId);
    },
    [clientId, setSortBy, fetchTransactions],
  );

  const handleRowClick = useCallback((tx: TransactionDto) => {
    setSelectedTransaction(tx);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedTransaction(null);
  }, []);

  const handleEventsClick = useCallback((tx: TransactionDto) => {
    const target = eventsTargetFromTransaction(tx);
    if (target) setEventsTarget(target);
  }, []);

  const handleCloseEvents = useCallback(() => {
    setEventsTarget(null);
  }, []);

  return (
    <div data-testid="history-tab">
      <div className="mb-4 flex justify-end">
        <NewOperationButton clientId={clientId} size="sm" />
      </div>

      {error && (
        <div
          className="mb-4 flex items-center justify-between rounded-lg border border-[#ffcdd2] bg-[#ffebee] px-4 py-3"
          role="alert"
          data-testid="history-error-banner"
        >
          <span className="text-[13px] text-[#c62828]">{error}</span>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void fetchTransactions(clientId)}
              data-testid="history-retry-btn"
            >
              Retry
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearError}
              data-testid="history-dismiss-error"
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}

      <HistoryFiltersPanel
        filters={filters}
        onFilterChange={setFilters}
        onClearFilters={handleClearFilters}
        onSearch={handleSearch}
        dateError={dateError}
      />

      <HistoryTable
        transactions={transactions}
        isLoading={isLoading}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        onRowClick={handleRowClick}
        onEventsClick={handleEventsClick}
        searchQuery={filters.searchQuery}
        onClearSearch={handleClearSearch}
      />

      {total > 0 && (
        <Pagination
          pageIndex={page}
          pageSize={limit}
          totalRows={total}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          className="mt-2"
        />
      )}

      {/* Detail modal */}
      <HistoryDetailModal
        transaction={selectedTransaction}
        isOpen={modalOpen}
        onClose={handleCloseModal}
      />

      <Modal
        isOpen={eventsTarget !== null}
        title="Transaction events"
        onClose={handleCloseEvents}
        className="max-w-2xl"
        footer={
          <Button variant="outline" onClick={handleCloseEvents}>
            Close
          </Button>
        }
      >
        <div data-testid="event-timeline-modal-body">
          {eventsTarget !== null && (
            <EventTimeline kind={eventsTarget.kind} parentId={eventsTarget.parentId} />
          )}
        </div>
      </Modal>
    </div>
  );
}
